import { ChangeDetectionStrategy, Component, inject, input, numberAttribute, ViewEncapsulation } from '@angular/core';
import { CalScanCloudIframeWrapperDirective, ICalScanCloudParams, ICalScanError } from '@calsale/scanovate';
import { IHttpStatus } from '@partners/api';
import { TFeature } from '@partners/settings';
import { takeOnce } from '@partners/utils';
import { catchError, map, Observable, switchMap, throwError } from 'rxjs';
import { ScanFacade } from '../../../facades';
import { BaseFeatureView } from '../../../feature/components/base-feature-view/base-feature-view.component';

@Component({
  selector: 'scan',
  standalone: true,
  imports: [CalScanCloudIframeWrapperDirective],
  template: `
    @if(selectedDocId){
    <div
      gtmFunnelStepEvent="funnelStepEvent"
      calScanCloudIframeWrapper
      [docCode]="selectedDocId()"
      (scanComplete)="handleScanCompletion($event)"
      (handleScanError)="handleScanError($event)"
    ></div>
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScanComponent extends BaseFeatureView {
  selectedDocId = input(0, { transform: numberAttribute });

  #scanFacade = inject(ScanFacade);

  #validateAndSaveScannedDocuments(): Observable<boolean> {
    return this.#scanFacade.validateScannedDocument().pipe(
      switchMap(({ screenName }: IHttpStatus) =>
        this.#scanFacade.saveScannedDocuments().pipe(map(() => screenName as TFeature))
      ),
      switchMap((screenName: TFeature) => this._navAdapter.navigateAfterScan(screenName as TFeature))
    );
  }
  /**
   * @description
   * here we will handle the after scan process.
   * it means that the ocr iframe was redirected it self to our url and emitted our Output callback
   * and by that declared that the scan process was ended.
   * after a successful or not scan process we will get params object made of a token and process id.
   * we will use the token to get from calsale server the actual data from the scan process.
   * then we call scan validate api and if successful we proceed to save scanned documents.
   * finally we navigate to the appropriate page.
   * @param params
   * @param selectedDocId
   */
  handleScanCompletion(params: ICalScanCloudParams | Error) {
    const selectedDocId = this.selectedDocId();
    this.#scanFacade
      .handleScanCompletion(params, selectedDocId)
      .pipe(
        takeOnce(),
        switchMap(({ screenName }: IHttpStatus) => {
          /** If screenName is present, navigate to the corresponding feature */
          if (screenName) {
            return this._navAdapter.navigateAfterScan(screenName as TFeature);
          }
          /** If no specific screen is returned, proceed with validating and saving the scanned documents */
          return this.#validateAndSaveScannedDocuments();
        }),
        catchError((error: ICalScanError) => {
          return throwError(() => error);
        })
      )
      .subscribe();
  }
  handleScanError(error: any) {
    this.#scanFacade.handleScanError(error);
  }
}
